import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, MapPin, Clock, Calendar, Download, User, Save, Loader2, Briefcase, Heart, Camera, Shield, Hash, Trash2, ChevronLeft, ChevronRight, CheckCircle2, XCircle, CalendarOff, Gift, Eye, EyeOff, KeyRound, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { compressImage } from '@/lib/imageUtils';
import { formatDate, formatTime, formatDateLong } from '@/lib/dateUtils';

const ALL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diff < 0) return null;
  const years = Math.floor(diff / 365);
  const months = Math.floor((diff % 365) / 30);
  const days = diff % 30;
  if (years > 0) return `${years}y ${months}m`;
  if (months > 0) return `${months}m ${days}d`;
  return `${days} days`;
}

export default function AdminEmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Schedule state
  const [checkinTime, setCheckinTime] = useState('09:00');
  const [checkoutTime, setCheckoutTime] = useState('18:00');
  const [workingDays, setWorkingDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);
  const [empGracePeriod, setEmpGracePeriod] = useState('');

  // Password management
  const [currentPassword, setCurrentPassword] = useState(null);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Details state
  const [joiningDate, setJoiningDate] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [currentAddress, setCurrentAddress] = useState('');
  const [sanctionedLeaves, setSanctionedLeaves] = useState(18);
  const [accumulatedLeaves, setAccumulatedLeaves] = useState(0);
  const [monthlyLeaveAllowance, setMonthlyLeaveAllowance] = useState(1.5);
  const [leavePolicyType, setLeavePolicyType] = useState('cumulative');
  const [department, setDepartment] = useState('');
  const [designation, setDesignation] = useState('');
  const [reportingManager, setReportingManager] = useState('');
  const [branch, setBranch] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactNumber, setEmergencyContactNumber] = useState('');
  const [pfNumber, setPfNumber] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [zones, setZones] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [zoneLoading, setZoneLoading] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [namePhoneSaving, setNamePhoneSaving] = useState(false);
  const [holidayCategory, setHolidayCategory] = useState('General');
  const [holidayCategories, setHolidayCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [rms, setRMs] = useState([]);
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calData, setCalData] = useState(null);
  const [calLoading, setCalLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [leaveBalData, setLeaveBalData] = useState(null);
  const [workPermits, setWorkPermits] = useState([]);
  const [permitsLoading, setPermitsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (id) fetchCalendar();
  }, [id, calMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [res, zonesRes, catRes] = await Promise.all([
        adminAPI.getEmployeeAttendance(id),
        adminAPI.getZones(),
        adminAPI.getHolidayCategories(),
      ]);
      setData(res.data);
      setZones(zonesRes.data || []);
      setHolidayCategories(catRes.data || []);
      const emp = res.data.employee || {};
      const schedule = emp.schedule || {};
      if (schedule.checkin_time) setCheckinTime(schedule.checkin_time);
      if (schedule.checkout_time) setCheckoutTime(schedule.checkout_time);
      if (schedule.working_days) setWorkingDays(schedule.working_days);
      if (schedule.grace_period_minutes != null) setEmpGracePeriod(String(schedule.grace_period_minutes));
      if (emp.joining_date) setJoiningDate(emp.joining_date);
      if (emp.gender) setGender(emp.gender);
      if (emp.dob) setDob(emp.dob);
      if (emp.current_address) setCurrentAddress(emp.current_address);
      if (emp.sanctioned_leaves != null) setSanctionedLeaves(emp.sanctioned_leaves);
      if (emp.accumulated_leaves != null) setAccumulatedLeaves(emp.accumulated_leaves);
      if (emp.monthly_leave_allowance != null) setMonthlyLeaveAllowance(emp.monthly_leave_allowance);
      if (emp.leave_policy_type) setLeavePolicyType(emp.leave_policy_type);
      if (emp.department) setDepartment(emp.department);
      if (emp.designation) setDesignation(emp.designation);
      if (emp.reporting_manager) setReportingManager(emp.reporting_manager);
      if (emp.branch) setBranch(emp.branch);
      if (emp.profile_photo) setProfilePhoto(emp.profile_photo);
      if (emp.emergency_contact_name) setEmergencyContactName(emp.emergency_contact_name);
      if (emp.emergency_contact_number) setEmergencyContactNumber(emp.emergency_contact_number);
      if (emp.pf_number) setPfNumber(emp.pf_number);
      if (emp.employee_number) setEmployeeNumber(emp.employee_number);
      setSelectedZones(emp.zone_ids || (emp.zone_id ? [emp.zone_id] : []));
      setEditName(emp.name || '');
      setEditPhone(emp.phone || '');
      if (emp.holiday_category) setHolidayCategory(emp.holiday_category);
      if (res.data.leave_balance) setLeaveBalData(res.data.leave_balance);
    } catch (err) {
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
    // Non-critical dropdown data — don't block main page
    try {
      const [branchRes, desigRes, rmRes] = await Promise.all([
        adminAPI.getBranches(),
        adminAPI.getDesignations(),
        adminAPI.getRMs(),
      ]);
      setBranches(branchRes.data || []);
      setDesignations(desigRes.data || []);
      setRMs(rmRes.data || []);
    } catch { /* dropdown data optional */ }
  };

  const fetchCalendar = async () => {
    setCalLoading(true);
    setSelectedDay(null);
    try {
      const res = await adminAPI.getEmployeeMonthly(id, calMonth);
      setCalData(res.data);
    } catch { /* silent */ }
    finally { setCalLoading(false); }
  };

  const fetchWorkPermits = async () => {
    setPermitsLoading(true);
    try {
      const res = await adminAPI.getWorkPermits({ employee_id: id });
      setWorkPermits(res.data.permits || []);
    } catch { /* silent */ }
    finally { setPermitsLoading(false); }
  };

  useEffect(() => { if (id) fetchWorkPermits(); }, [id]);

  const handleEditPermitHours = async (permitId, newHours) => {
    const h = parseFloat(newHours);
    if (!h || h <= 0) { toast.error('Enter valid hours'); return; }
    try {
      await adminAPI.editWorkPermit(permitId, { hours: h });
      toast.success('Permit hours updated');
      fetchWorkPermits();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDeletePermit = async (permitId) => {
    if (!window.confirm('Delete this work permit? This will affect comp-off balance.')) return;
    try {
      await adminAPI.deleteWorkPermit(permitId);
      toast.success('Permit deleted');
      fetchWorkPermits();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleSaveNamePhone = async () => {
    if (!editName.trim()) { toast.error('Name is required'); return; }
    if (!editPhone.trim()) { toast.error('Phone is required'); return; }
    setNamePhoneSaving(true);
    try {
      const res = await adminAPI.updateEmployeeDetails(id, { name: editName.trim(), phone: editPhone.trim() });
      setEmp(res.data);
      toast.success('Name/Phone updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setNamePhoneSaving(false);
    }
  };

  const handleDeleteAttendance = async (recordId) => {
    try {
      await adminAPI.deleteAttendance(recordId);
      toast.success('Attendance record deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };



  const handleSaveSchedule = async () => {
    setScheduleLoading(true);
    try {
      await adminAPI.updateEmployeeSchedule(id, {
        checkin_time: checkinTime,
        checkout_time: checkoutTime,
        working_days: workingDays,
        grace_period_minutes: empGracePeriod !== '' ? parseInt(empGracePeriod) : -1
      });
      toast.success('Schedule saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    setDetailsLoading(true);
    try {
      await adminAPI.updateEmployeeDetails(id, {
        joining_date: joiningDate || null,
        gender: gender || null,
        dob: dob || null,
        current_address: currentAddress || null,
        sanctioned_leaves: sanctionedLeaves,
        accumulated_leaves: accumulatedLeaves,
        monthly_leave_allowance: monthlyLeaveAllowance,
        leave_policy_type: leavePolicyType,
        department: department || null,
        designation: designation || null,
        reporting_manager: reportingManager || null,
        branch: branch || null,
        profile_photo: profilePhoto || null,
        emergency_contact_name: emergencyContactName || null,
        emergency_contact_number: emergencyContactNumber || null,
        pf_number: pfNumber || null,
        employee_number: employeeNumber || null,
        holiday_category: holidayCategory || 'General'
      });
      toast.success('Employee details saved!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchPassword = async () => {
    setPwLoading(true);
    try {
      const res = await adminAPI.getEmployeePassword(id);
      setCurrentPassword(res.data.password || res.data.message || 'Not available');
      setShowCurrentPw(true);
    } catch { toast.error('Failed to fetch password'); }
    finally { setPwLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResetLoading(true);
    try {
      await adminAPI.resetEmployeePassword(id, newPassword);
      toast.success('Password reset successfully!');
      setNewPassword('');
      setCurrentPassword(newPassword);
      setShowCurrentPw(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
    finally { setResetLoading(false); }
  };


  const toggleDay = (day) => {
    setWorkingDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleExport = async () => {
    try {
      const res = await adminAPI.exportAttendance(null, id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${data?.employee?.name || id}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported!');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be under 2MB');
      return;
    }
    const compressed = await compressImage(file, { maxWidth: 300, maxHeight: 300, quality: 0.3 });
    setProfilePhoto(compressed);
  };

  const handleZoneToggle = async (zoneId) => {
    const newZones = selectedZones.includes(zoneId)
      ? selectedZones.filter(id => id !== zoneId)
      : [...selectedZones, zoneId];
    setZoneLoading(true);
    try {
      await adminAPI.updateEmployeeZone(id, newZones);
      setSelectedZones(newZones);
      toast.success(newZones.length ? `${newZones.length} zone(s) assigned` : 'All zones removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update zones');
    } finally {
      setZoneLoading(false);
    }
  };

  const emp = data?.employee || {};
  const tenure = daysSince(emp.joining_date);

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="h-10 w-10 rounded-xl bg-white border border-border/40 flex items-center justify-center hover:bg-[#f4f4f5] transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 data-testid="employee-detail-title" className="text-xl font-extrabold text-[#1a2529] tracking-tight">Employee Details</h1>
          </div>
          <Button data-testid="export-employee-btn" onClick={handleExport} variant="outline" size="sm" className="gap-1.5 font-medium">
            <Download size={14} /> Export
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data ? (
          <>
            {/* Employee Info Card */}
            <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  {profilePhoto ? (
                    <img src={profilePhoto} alt={emp.name} className="h-14 w-14 rounded-full object-cover border-2 border-[#026536]/20" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-[#026536]/10 flex items-center justify-center">
                      <User className="text-[#026536]" size={28} />
                    </div>
                  )}
                  <label className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                    <Camera size={16} className="text-white" />
                    <input data-testid="admin-photo-upload" type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  </label>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="edit-emp-name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="text-lg font-bold text-[#1a2529] bg-transparent border-b border-transparent hover:border-border/40 focus:border-[#026536] outline-none w-full transition-colors"
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Phone size={12} className="text-[#1a2529]/40" />
                      <input
                        data-testid="edit-emp-phone"
                        value={editPhone}
                        onChange={(e) => setEditPhone(e.target.value)}
                        className="text-sm text-[#1a2529]/60 bg-transparent border-b border-transparent hover:border-border/40 focus:border-[#026536] outline-none w-32 transition-colors"
                      />
                    </div>
                    {(editName !== emp.name || editPhone !== emp.phone) && (
                      <Button
                        data-testid="save-name-phone-btn"
                        onClick={handleSaveNamePhone}
                        disabled={namePhoneSaving}
                        size="sm"
                        className="h-6 text-[10px] bg-[#026536] hover:bg-[#026536]/90 gap-1"
                      >
                        {namePhoneSaving ? <Loader2 size={10} className="animate-spin" /> : null}
                        Save
                      </Button>
                    )}
                    <Badge className={emp.status === 'approved' ? 'bg-[#026536]/10 text-[#026536] border-0 text-xs' : 'bg-[#dea439]/10 text-[#dea439] border-0 text-xs'}>
                      {emp.status}
                    </Badge>
                  </div>
                  {(emp.designation || emp.department) && (
                    <p className="text-xs text-[#1a2529]/50 mt-1">
                      {emp.designation}{emp.designation && emp.department ? ' · ' : ''}{emp.department}
                      {emp.branch ? <span className="text-[#1a2529]/30"> · {emp.branch}</span> : null}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-[#f4f4f5] px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">Check-ins</p>
                  <p className="text-lg font-extrabold text-[#1a2529]">{data.total}</p>
                </div>
                {tenure && (
                  <div className="bg-[#026536]/5 px-3 py-2 rounded-lg">
                    <p className="text-[10px] text-[#026536]/60 font-semibold uppercase">Tenure</p>
                    <p data-testid="emp-tenure" className="text-lg font-extrabold text-[#026536]">{tenure}</p>
                  </div>
                )}
                <div className="bg-[#f4f4f5] px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">Leave Policy</p>
                  <p className="text-sm font-extrabold text-[#1a2529]">
                    {emp.leave_policy_type === 'monthly' ? 'Monthly' : 'Cumulative'}
                  </p>
                  <p className="text-[9px] text-[#1a2529]/30">
                    {emp.monthly_leave_allowance ?? 1.5}/mo
                    {emp.leave_policy_type !== 'monthly' && emp.accumulated_leaves ? ` + ${emp.accumulated_leaves} banked` : ''}
                  </p>
                </div>
                <div className="bg-[#f4f4f5] px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">Joined</p>
                  <p className="text-sm font-semibold text-[#1a2529]">
                    {emp.joining_date ? formatDate(emp.joining_date) : 'Not set'}
                  </p>
                </div>
              </div>
            </div>

            {/* Zone Assignment */}
            <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
              <h3 className="font-bold text-[#1a2529] text-sm mb-3 flex items-center gap-2">
                <MapPin size={16} className="text-[#026536]" /> Assigned Geo Zones
              </h3>
              {zones.length === 0 ? (
                <p className="text-xs text-[#1a2529]/40">No zones created yet. <button onClick={() => navigate('/admin/zones')} className="text-[#026536] font-semibold hover:underline">Create one</button></p>
              ) : (
                <div className="space-y-2">
                  {zones.map(z => (
                    <label key={z.id} data-testid={`zone-checkbox-${z.id}`} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#f4f4f5] cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedZones.includes(z.id)}
                        onChange={() => handleZoneToggle(z.id)}
                        disabled={zoneLoading}
                        className="h-4 w-4 rounded border-[#1a2529]/20 text-[#026536] focus:ring-[#026536]"
                      />
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: z.color || '#026536' }} />
                        <span className="text-sm font-medium text-[#1a2529] truncate">{z.name}</span>
                      </div>
                      {z.radius_meters && (
                        <span className="text-[10px] text-[#1a2529]/30 shrink-0">{Math.round(z.radius_meters)}m</span>
                      )}
                    </label>
                  ))}
                  {selectedZones.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {selectedZones.map(zid => {
                        const z = zones.find(zone => zone.id === zid);
                        return z ? (
                          <Badge key={zid} className="bg-[#026536]/10 text-[#026536] border-0 text-[10px]">{z.name}</Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              )}
              <p className="text-[10px] text-[#1a2529]/30 mt-2">Supervisory staff can be assigned to multiple zones. Check-ins outside all assigned zones will be flagged.</p>
            </div>

            {/* Personal Details */}
            <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
              <h3 className="font-bold text-[#1a2529] text-sm mb-4 flex items-center gap-2">
                <User size={16} className="text-[#026536]" /> Personal Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Joining Date</Label>
                  <Input data-testid="joining-date-input" type="date" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Date of Birth</Label>
                  <Input data-testid="dob-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Gender</Label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger data-testid="gender-select" className="h-10 mt-1"><SelectValue placeholder="Select gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Department</Label>
                  <Input data-testid="department-input" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g., Field Operations" className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Designation</Label>
                  <select data-testid="designation-input" value={designation} onChange={(e) => setDesignation(e.target.value)}
                    className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                    <option value="">Select designation</option>
                    {designations.map(d => <option key={d} value={d}>{d}</option>)}
                    {designation && !designations.includes(designation) && <option value={designation}>{designation}</option>}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Reporting Manager</Label>
                  <select data-testid="reporting-manager-input" value={reportingManager} onChange={(e) => setReportingManager(e.target.value)}
                    className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                    <option value="">Select RM</option>
                    {rms.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                    {reportingManager && !rms.some(r => r.name === reportingManager) && <option value={reportingManager}>{reportingManager}</option>}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Branch / HH</Label>
                  <select data-testid="branch-input" value={branch} onChange={(e) => setBranch(e.target.value)}
                    className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                    <option value="">Select branch</option>
                    {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                    {branch && !branches.some(b => b.name === branch) && <option value={branch}>{branch}</option>}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Leave Policy</Label>
                  <select
                    data-testid="leave-policy-select"
                    value={leavePolicyType}
                    onChange={(e) => setLeavePolicyType(e.target.value)}
                    className="w-full mt-1 h-10 px-3 border border-border rounded-lg text-sm bg-white"
                  >
                    <option value="cumulative">Cumulative (Payroll)</option>
                    <option value="monthly">Monthly (Contract)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Accumulated Leaves</Label>
                  <Input data-testid="accumulated-leaves-input" type="number" min={0} step={0.25} value={accumulatedLeaves} onChange={(e) => setAccumulatedLeaves(parseFloat(e.target.value) || 0)} className="h-10 mt-1" />
                  <p className="text-[9px] text-[#1a2529]/30 mt-0.5">Carried-over / banked balance</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Monthly Allowance</Label>
                  <Input data-testid="monthly-allowance-input" type="number" min={0} step={0.25} value={monthlyLeaveAllowance} onChange={(e) => setMonthlyLeaveAllowance(parseFloat(e.target.value) || 0)} className="h-10 mt-1" />
                  <p className="text-[9px] text-[#1a2529]/30 mt-0.5">Leaves accrued per month</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Balance Remaining</Label>
                  <div className="h-10 mt-1 flex items-center px-3 bg-[#f4f4f5] rounded-lg border border-border">
                    <span data-testid="computed-available-leaves" className="text-sm font-bold text-[#026536]">
                      {leaveBalData ? leaveBalData.remaining : '—'} days
                    </span>
                  </div>
                  <p className="text-[9px] text-[#1a2529]/30 mt-0.5">
                    {leaveBalData
                      ? `Total ${leaveBalData.total_available} − Used ${leaveBalData.used} − Pending ${leaveBalData.pending}`
                      : 'Save to compute'}
                  </p>
                </div>
              </div>
              <div className="mb-4">
                <Label className="text-xs font-semibold text-[#1a2529]/70">Current Address</Label>
                <Input data-testid="address-input" value={currentAddress} onChange={(e) => setCurrentAddress(e.target.value)} placeholder="Full address" className="h-10 mt-1" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Employee Number</Label>
                  <Input data-testid="employee-number-input" value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)} placeholder="e.g., EMP001" className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">PF Number</Label>
                  <Input data-testid="pf-number-input" value={pfNumber} onChange={(e) => setPfNumber(e.target.value)} placeholder="e.g., PF12345" className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Emergency Contact Name</Label>
                  <Input data-testid="emergency-name-input" value={emergencyContactName} onChange={(e) => setEmergencyContactName(e.target.value)} placeholder="Contact person name" className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Emergency Contact Number</Label>
                  <Input data-testid="emergency-number-input" value={emergencyContactNumber} onChange={(e) => setEmergencyContactNumber(e.target.value)} placeholder="10-digit number" className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Holiday Category</Label>
                  <select data-testid="holiday-category-select" value={holidayCategory} onChange={(e) => setHolidayCategory(e.target.value)}
                    className="w-full h-10 mt-1 border rounded-md px-3 text-sm bg-white">
                    {holidayCategories.map(c => <option key={c.id || c} value={c.name || c}>{c.name || c}</option>)}
                  </select>
                </div>
              </div>
              <Button data-testid="save-details-btn" onClick={handleSaveDetails} disabled={detailsLoading} className="h-10 bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-2">
                {detailsLoading ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                Save Details
              </Button>
            </div>

            {/* Password & Security */}
            <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
              <h3 className="font-bold text-[#1a2529] text-sm mb-4 flex items-center gap-2">
                <KeyRound size={16} className="text-[#026536]" /> Password & Security
              </h3>
              <div className="mb-4">
                <Label className="text-xs font-semibold text-[#1a2529]/70">Current Password</Label>
                <div className="flex items-center gap-2 mt-1">
                  {showCurrentPw && currentPassword ? (
                    <div className="flex items-center gap-2 flex-1">
                      <code data-testid="current-password-display" className="bg-[#f4f4f5] px-3 py-2 rounded-lg text-sm font-mono flex-1">{currentPassword}</code>
                      <button data-testid="hide-password-btn" onClick={() => { setShowCurrentPw(false); setCurrentPassword(null); }} className="text-[#1a2529]/40 hover:text-[#1a2529]/60">
                        <EyeOff size={16} />
                      </button>
                    </div>
                  ) : (
                    <Button data-testid="view-password-btn" onClick={fetchPassword} disabled={pwLoading} variant="outline" className="h-9 text-xs gap-1.5">
                      {pwLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                      View Password
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1a2529]/70">Reset Password</Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input data-testid="new-password-input" type={showNewPw ? 'text' : 'password'} value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password (min 6 chars)" className="h-10 pr-10" />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#1a2529]/30 hover:text-[#1a2529]/60">
                      {showNewPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <Button data-testid="reset-password-btn" onClick={handleResetPassword} disabled={resetLoading || !newPassword}
                    className="h-10 bg-red-500 hover:bg-red-600 text-white gap-1.5 font-semibold">
                    {resetLoading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                    Reset
                  </Button>
                </div>
                <p className="text-[10px] text-[#1a2529]/30 mt-1">This will immediately change the employee's login password.</p>
              </div>
            </div>

            {/* Work Schedule */}
            <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
              <h3 className="font-bold text-[#1a2529] text-sm mb-4 flex items-center gap-2">
                <Clock size={16} className="text-[#026536]" /> Work Schedule
              </h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Check-in Time</Label>
                  <Input data-testid="checkin-time-input" type="time" value={checkinTime} onChange={(e) => setCheckinTime(e.target.value)} className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Check-out Time</Label>
                  <Input data-testid="checkout-time-input" type="time" value={checkoutTime} onChange={(e) => setCheckoutTime(e.target.value)} className="h-10 mt-1" />
                </div>
              </div>
              <div className="mb-4">
                <Label className="text-xs font-semibold text-[#1a2529]/70 mb-2 block">Working Days</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_DAYS.map(day => (
                    <label key={day} data-testid={`day-${day.toLowerCase()}`} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm font-medium ${workingDays.includes(day) ? 'bg-[#026536] text-white border-[#026536]' : 'bg-white text-[#1a2529]/60 border-border hover:border-[#026536]/30'}`}>
                      <input type="checkbox" checked={workingDays.includes(day)} onChange={() => toggleDay(day)} className="sr-only" />
                      {day}
                    </label>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <Label className="text-xs font-semibold text-[#1a2529]/70">Late Grace Period (minutes)</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Input data-testid="emp-grace-period-input" type="number" min={0} max={120}
                    value={empGracePeriod} onChange={(e) => setEmpGracePeriod(e.target.value)}
                    placeholder="Use company default" className="h-10 w-40" />
                  <span className="text-[10px] text-[#1a2529]/40">Leave blank to use the company-wide setting</span>
                </div>
              </div>
              <Button data-testid="save-schedule-btn" onClick={handleSaveSchedule} disabled={scheduleLoading} className="h-10 bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-2">
                {scheduleLoading ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                Save Schedule
              </Button>
            </div>

            {/* Monthly Attendance Calendar */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#1a2529]">Monthly Attendance</h3>
                <div className="flex items-center gap-2">
                  <button data-testid="cal-prev-month" onClick={() => {
                    const [y, m] = calMonth.split('-').map(Number);
                    const d = new Date(y, m - 2, 1);
                    setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
                  }} className="h-7 w-7 rounded-lg bg-[#f4f4f5] flex items-center justify-center hover:bg-[#026536]/10 transition-colors">
                    <ChevronLeft size={14} />
                  </button>
                  <span className="text-xs font-bold text-[#1a2529] min-w-[100px] text-center">
                    {(() => { const [y, m] = calMonth.split('-').map(Number); return new Date(y, m - 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }); })()}
                  </span>
                  <button data-testid="cal-next-month" onClick={() => {
                    const [y, m] = calMonth.split('-').map(Number);
                    const d = new Date(y, m, 1);
                    setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
                  }} className="h-7 w-7 rounded-lg bg-[#f4f4f5] flex items-center justify-center hover:bg-[#026536]/10 transition-colors">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              {calLoading ? (
                <div className="flex justify-center py-8"><div className="h-6 w-6 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" /></div>
              ) : calData ? (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="grid grid-cols-5 gap-1.5">
                    {[
                      { label: 'Present', val: calData.summary?.present, color: 'text-[#026536]', bg: 'bg-[#026536]/10' },
                      { label: 'Absent', val: calData.summary?.absent, color: 'text-red-500', bg: 'bg-red-50' },
                      { label: 'Late', val: calData.summary?.late, color: 'text-[#dea439]', bg: 'bg-[#dea439]/10' },
                      { label: 'Leave', val: calData.summary?.leave, color: 'text-[#dea439]', bg: 'bg-[#dea439]/10' },
                      { label: 'Holiday', val: calData.summary?.holiday, color: 'text-purple-600', bg: 'bg-purple-50' },
                    ].map(s => (
                      <div key={s.label} className={`${s.bg} rounded-lg p-2 text-center`}>
                        <span className={`text-base font-extrabold ${s.color}`}>{s.val}</span>
                        <p className={`text-[8px] font-bold uppercase ${s.color} opacity-50`}>{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div className="bg-white rounded-xl p-3 border border-border/30 shadow-sm">
                    <div className="grid grid-cols-7 gap-1 mb-1.5">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                        <div key={d} className="text-center text-[9px] font-bold text-[#1a2529]/25 uppercase">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {(() => {
                        if (!calData?.days?.length) return null;
                        const firstDay = new Date(calData.days[0]?.date);
                        let dow = firstDay.getDay();
                        dow = dow === 0 ? 6 : dow - 1;
                        const grid = Array(dow).fill(null);
                        return [...grid, ...calData.days].map((day, idx) => {
                          if (!day) return <div key={`e-${idx}`} />;
                          const STATUS = { present: 'bg-[#026536] text-white', absent: 'bg-red-500 text-white', leave: 'bg-[#dea439] text-white', holiday: 'bg-purple-500 text-white', weekend: 'bg-[#1a2529]/10 text-[#1a2529]/30', future: 'bg-transparent text-[#1a2529]/15' };
                          const dayNum = parseInt(day.date.split('-')[2]);
                          const isSelected = selectedDay?.date === day.date;
                          return (
                            <button key={day.date} onClick={() => setSelectedDay(isSelected ? null : day)} data-testid={`cal-day-${day.date}`}
                              className={`aspect-square rounded text-[11px] font-bold flex items-center justify-center transition-all ${STATUS[day.status] || STATUS.future} ${isSelected ? 'ring-2 ring-[#026536] ring-offset-1 scale-110' : ''} ${day.status !== 'future' ? 'hover:scale-105 cursor-pointer' : 'cursor-default'}`}
                            >{dayNum}</button>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {/* Day Detail */}
                  {selectedDay && selectedDay.status !== 'future' && (
                    <div data-testid="cal-day-detail" className="bg-white rounded-xl p-3 border border-border/30 shadow-sm animate-slide-up">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-bold text-xs text-[#1a2529]">
                          {formatDateLong(selectedDay.date)}
                        </p>
                        <Badge className={`border-0 text-[9px] ${selectedDay.status === 'present' ? 'bg-[#026536]/10 text-[#026536]' : selectedDay.status === 'absent' ? 'bg-red-50 text-red-500' : selectedDay.status === 'leave' ? 'bg-[#dea439]/10 text-[#dea439]' : 'bg-purple-50 text-purple-600'}`}>
                          {selectedDay.status}
                        </Badge>
                      </div>
                      {selectedDay.status === 'present' && (
                        <div className="flex items-center gap-4 text-xs">
                          <div>
                            <span className="text-[#1a2529]/40 text-[10px] font-semibold">In: </span>
                            <span className="font-bold text-[#026536]">{formatTime(selectedDay.checkin_time)}</span>
                          </div>
                          <div>
                            <span className="text-[#1a2529]/40 text-[10px] font-semibold">Out: </span>
                            <span className="font-bold text-[#1a2529]">{selectedDay.checkout_time ? formatTime(selectedDay.checkout_time) : '-'}</span>
                          </div>
                          {selectedDay.is_late && <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[9px]">Late</Badge>}
                          {selectedDay.attendance_id && (
                            <button
                              data-testid="delete-attendance-btn"
                              onClick={() => {
                                if (window.confirm(`Delete attendance record for ${formatDateLong(selectedDay.date)}? This cannot be undone.`)) {
                                  handleDeleteAttendance(selectedDay.attendance_id);
                                  setSelectedDay(null);
                                }
                              }}
                              className="ml-auto text-[10px] text-red-500 font-semibold hover:underline flex items-center gap-0.5"
                            >
                              <Trash2 size={10} /> Delete
                            </button>
                          )}
                        </div>
                      )}
                      {selectedDay.status === 'holiday' && <p className="text-xs text-purple-600">{selectedDay.holiday_name}</p>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                  <Calendar className="mx-auto text-[#1a2529]/15 mb-3" size={36} />
                  <p className="font-medium text-[#1a2529]/50">No records yet</p>
                </div>
              )}
            </div>

            {/* Work Permits Management */}
            <div data-testid="work-permits-section" className="bg-white rounded-xl p-5 shadow-sm border border-border/30">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-[#1a2529] flex items-center gap-2">
                  <Shield size={16} className="text-blue-600" /> Work Permits
                </h3>
                <Badge className="bg-blue-50 text-blue-600 border-0 text-[10px]">{workPermits.length}</Badge>
              </div>
              {permitsLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#026536]" size={20} /></div>
              ) : workPermits.length === 0 ? (
                <p className="text-sm text-[#1a2529]/40 text-center py-4">No work permits</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {workPermits.map(p => (
                    <div key={p.id} data-testid={`admin-permit-${p.id}`} className="bg-[#f4f4f5] rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-[#1a2529] capitalize">{p.permission_type?.replace('_', ' ')}</span>
                          <Badge className={`border-0 text-[9px] font-bold capitalize ${p.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : p.status === 'rejected' ? 'bg-red-50 text-red-500' : 'bg-[#dea439]/10 text-[#dea439]'}`}>{p.status}</Badge>
                        </div>
                        <span className="text-[10px] text-[#1a2529]/40">{formatDate(p.date)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#1a2529]/60">Hours: <b>{p.hours || 0}h</b></span>
                          {p.reason && <span className="text-[10px] text-[#1a2529]/30 italic truncate max-w-[150px]">{p.reason}</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`edit-permit-hours-${p.id}`}
                            onClick={() => {
                              const newH = prompt(`Edit hours for ${p.permission_type} on ${p.date} (current: ${p.hours}h):`, p.hours);
                              if (newH !== null) handleEditPermitHours(p.id, newH);
                            }}
                            className="text-[10px] text-blue-600 font-semibold hover:underline"
                          >
                            Edit
                          </button>
                          <span className="text-[#1a2529]/20">|</span>
                          <button
                            data-testid={`delete-permit-${p.id}`}
                            onClick={() => handleDeletePermit(p.id)}
                            className="text-[10px] text-red-500 font-semibold hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}
